"use client";

import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Card,
  CardBody,
  Grid,
  GridItem,
  Badge,
  Button,
  Divider,
  HStack,
  Icon,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  SimpleGrid,
  List,
  ListItem,
  ListIcon,
} from "@chakra-ui/react";
import {
  FiCreditCard,
  FiDollarSign,
  FiCalendar,
  FiCheckCircle,
  FiExternalLink,
} from "react-icons/fi";
import { useAuth } from "../contexts/AuthContext";
import {
  BILLING_PLANS,
  getPlanDisplayFromSubscription,
  getPlanFromSubscriptionType,
  type PlanInterval,
  type BillingPlan,
} from "../lib/billingPlans";

import { API_BASE_URL as API_URL } from "../config";

interface Subscription {
  company_name: string;
  stripe_customer_id: string;
  stripe_subscription_id: string;
  stripe_price_id?: string;
  subscription_type?: PlanInterval;
  status: string;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
}

interface Invoice {
  stripe_invoice_id: string;
  amount_paid: number;
  currency: string;
  status: string;
  paid_at: string;
  invoice_pdf: string;
  receipt_url: string;
  hosted_invoice_url: string;
}

function PlanFeatures({ features }: { features: string[] }) {
  return (
    <List spacing={2}>
      {features.map((feature) => (
        <ListItem key={feature} fontSize="sm" color="gray.700">
          <ListIcon as={FiCheckCircle} color="green.500" />
          {feature}
        </ListItem>
      ))}
    </List>
  );
}

function PlanPricingCard({
  plan,
  isSelected,
  onSelect,
  onSubscribe,
  isLoading,
  actionLabel = "Subscribe",
}: {
  plan: BillingPlan;
  isSelected?: boolean;
  onSelect?: () => void;
  onSubscribe: () => void;
  isLoading: boolean;
  actionLabel?: string;
}) {
  return (
    <Card
      variant="outline"
      borderWidth={2}
      borderColor={isSelected ? "primary.500" : "gray.200"}
      bg={isSelected ? "primary.50" : "white"}
      h="100%"
      cursor={onSelect ? "pointer" : "default"}
      onClick={onSelect}
      transition="all 0.2s"
      _hover={
        onSelect ? { borderColor: "primary.400", shadow: "md" } : undefined
      }
    >
      <CardBody>
        <VStack align="stretch" spacing={4} h="100%">
          <HStack justify="space-between" align="start">
            <Box>
              <Heading size="md">{plan.name}</Heading>
              {plan.subtitle && (
                <Text fontSize="sm" color="gray.600" mt={1}>
                  {plan.subtitle}
                </Text>
              )}
            </Box>
            {plan.badge && (
              <Badge colorScheme="green" fontSize="xs">
                {plan.badge}
              </Badge>
            )}
          </HStack>

          <Box>
            <HStack align="baseline" spacing={1}>
              <Text fontSize="3xl" fontWeight="bold" color="primary.600">
                {plan.priceLabel}
              </Text>
              <Text fontSize="md" color="gray.600">
                {plan.interval}
              </Text>
            </HStack>
          </Box>

          <Text
            fontSize="sm"
            color={plan.name === "Yearly Plan" ? "green.600" : "gray.600"}
          >
            {plan.description}
          </Text>

          <Divider />

          <Box flex="1">
            <PlanFeatures features={plan.features} />
          </Box>

          <Button
            variant="brandGradient"
            size="lg"
            w="100%"
            onClick={(e) => {
              e.stopPropagation();
              onSubscribe();
            }}
            isLoading={isLoading}
            leftIcon={<FiCreditCard />}
          >
            {actionLabel}
          </Button>
        </VStack>
      </CardBody>
    </Card>
  );
}

export default function Billing() {
  const { user } = useAuth();
  const toast = useToast();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<PlanInterval | null>(
    null,
  );
  const [cancelLoading, setCancelLoading] = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanInterval>("monthly");

  const fetchBillingData = useCallback(async () => {
    try {
      setLoading(true);

      const subResponse = await fetch(`${API_URL}/api/billing/subscription`, {
        headers: {
          "user-id": user?.id || "",
        },
      });

      if (subResponse.ok) {
        const subData = await subResponse.json();
        setSubscription(subData.subscription);
        const activePlan = getPlanFromSubscriptionType(
          subData.subscription?.subscription_type,
        );
        if (activePlan) {
          setSelectedPlan(activePlan);
        }
      }

      const invoicesResponse = await fetch(`${API_URL}/api/billing/invoices`, {
        headers: {
          "user-id": user?.id || "",
        },
      });

      if (invoicesResponse.ok) {
        const invoicesData = await invoicesResponse.json();
        setInvoices(invoicesData.invoices || []);
      }
    } catch (error) {
      console.error("Error fetching billing data:", error);
      toast({
        title: "Error",
        description: "Failed to load billing data",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [user?.id, toast]);

  useEffect(() => {
    if (user?.id) {
      fetchBillingData();
    }
  }, [user?.id, fetchBillingData]);

  const handleSubscribe = async (plan: PlanInterval) => {
    try {
      setCheckoutLoading(plan);

      const response = await fetch(
        `${API_URL}/api/billing/create-subscription-checkout`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "user-id": user?.id || "",
          },
          body: JSON.stringify({ plan }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to create checkout session");
      }

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast({
        title: "Error",
        description: "Failed to start checkout process",
        status: "error",
        duration: 3000,
      });
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (
      !window.confirm(
        "Are you sure you want to cancel your subscription? You will retain access until the end of the billing period.",
      )
    ) {
      return;
    }

    try {
      setCancelLoading(true);

      const response = await fetch(
        `${API_URL}/api/billing/cancel-subscription`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "user-id": user?.id || "",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to cancel subscription");
      }

      toast({
        title: "Subscription Cancelled",
        description:
          "Your subscription will be cancelled at the end of the billing period",
        status: "success",
        duration: 5000,
      });

      fetchBillingData();
    } catch (error) {
      console.error("Error canceling subscription:", error);
      toast({
        title: "Error",
        description: "Failed to cancel subscription",
        status: "error",
        duration: 3000,
      });
    } finally {
      setCancelLoading(false);
    }
  };

  const handleReactivate = async () => {
    try {
      setReactivateLoading(true);

      const response = await fetch(
        `${API_URL}/api/billing/create-reactivation-checkout`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "user-id": user?.id || "",
          },
          body: JSON.stringify({ plan: selectedPlan }),
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.url) {
        throw new Error(
          data.error || "Failed to create reactivation checkout",
        );
      }

      window.location.href = data.url;
    } catch (error) {
      console.error("Error reactivating subscription:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to start reactivation checkout",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setReactivateLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getInvoicePlanName = (amountPaid: number) => {
    if (amountPaid >= 1300) return BILLING_PLANS.yearly.name;
    return BILLING_PLANS.monthly.name;
  };

  const isActive =
    subscription?.status === "active" || subscription?.status === "trialing";

  const activePlan = subscription
    ? getPlanDisplayFromSubscription(subscription)
    : null;

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <VStack spacing={4}>
          <Spinner size="xl" />
          <Text>Loading billing information...</Text>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="lg" mb={2}>
            Billing & Subscription
          </Heading>
          <Text color="gray.600">
            Manage your subscription and payment information
          </Text>
        </Box>

        {!subscription && (
          <Alert status="warning">
            <AlertIcon />
            <Box flex="1">
              <Text fontWeight="semibold">No Active Subscription</Text>
              <Text fontSize="sm">
                Choose a monthly or yearly plan to complete your setup and
                access your white-labeled AI Voice Agent instance.
              </Text>
            </Box>
          </Alert>
        )}

        {subscription?.cancel_at_period_end && (
          <Alert status="info">
            <AlertIcon />
            <Box flex="1">
              <HStack justify="space-between" align="center">
                <Box>
                  <Text fontWeight="semibold">Subscription Ending</Text>
                  <Text fontSize="sm">
                    Your subscription will end on{" "}
                    {formatDate(subscription.current_period_end)}
                  </Text>
                </Box>
                <Button
                  colorScheme="blue"
                  size="sm"
                  onClick={handleReactivate}
                  isLoading={reactivateLoading}
                >
                  Reactivate Subscription
                </Button>
              </HStack>
            </Box>
          </Alert>
        )}

        {subscription && activePlan ? (
          <Card>
            <CardBody>
              <VStack align="stretch" spacing={4}>
                <HStack justify="space-between">
                  <Box>
                    <Text fontSize="sm" color="gray.600" mb={1}>
                      Current Plan
                    </Text>
                    <Heading size="md">{activePlan.name}</Heading>
                  </Box>
                  <Badge
                    colorScheme={isActive ? "green" : "red"}
                    fontSize="md"
                    px={3}
                    py={1}
                  >
                    {subscription.status}
                  </Badge>
                </HStack>

                <Divider />

                <Grid
                  templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                  gap={6}
                >
                  <GridItem>
                    <HStack spacing={2} mb={2}>
                      <Icon as={FiDollarSign} color="blue.500" />
                      <Text fontSize="sm" color="gray.600">
                        Price
                      </Text>
                    </HStack>
                    <Text fontSize="xl" fontWeight="bold">
                      {activePlan.priceLabel}
                      {activePlan.interval}
                    </Text>
                  </GridItem>

                  <GridItem>
                    <HStack spacing={2} mb={2}>
                      <Icon as={FiCalendar} color="blue.500" />
                      <Text fontSize="sm" color="gray.600">
                        {subscription.cancel_at_period_end
                          ? "Ends On"
                          : "Next Billing Date"}
                      </Text>
                    </HStack>
                    <Text fontSize="xl" fontWeight="bold">
                      {formatDate(subscription.current_period_end)}
                    </Text>
                  </GridItem>
                </Grid>

                <Divider />

                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={3}>
                    Plan Features
                  </Text>
                  <PlanFeatures features={activePlan.features} />
                </Box>

                {!subscription.cancel_at_period_end && (
                  <HStack spacing={4} pt={4}>
                    <Button
                      colorScheme="red"
                      variant="outline"
                      onClick={handleCancelSubscription}
                      isLoading={cancelLoading}
                    >
                      Cancel Subscription
                    </Button>
                  </HStack>
                )}
              </VStack>
            </CardBody>
          </Card>
        ) : (
          <Box>
            <Heading size="md" mb={4}>
              Choose Your Plan
            </Heading>
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              {(Object.keys(BILLING_PLANS) as PlanInterval[]).map((planKey) => (
                <PlanPricingCard
                  key={planKey}
                  plan={BILLING_PLANS[planKey]}
                  isSelected={selectedPlan === planKey}
                  onSelect={() => setSelectedPlan(planKey)}
                  onSubscribe={() => handleSubscribe(planKey)}
                  isLoading={checkoutLoading === planKey}
                />
              ))}
            </SimpleGrid>
          </Box>
        )}

        {invoices.length > 0 && (
          <Card>
            <CardBody>
              <Heading size="md" mb={4}>
                Billing History
              </Heading>
              <TableContainer>
                <Table variant="simple" size="md">
                  <Thead>
                    <Tr>
                      <Th>Date</Th>
                      <Th>Description</Th>
                      <Th isNumeric>Amount</Th>
                      <Th>Status</Th>
                      <Th>Invoice</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {invoices.map((invoice, index) => (
                      <Tr key={index} _hover={{ bg: "gray.50" }}>
                        <Td>
                          <Text fontWeight="medium">
                            {formatDate(invoice.paid_at)}
                          </Text>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="medium">
                              {getInvoicePlanName(invoice.amount_paid)}
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                              Invoice #{invoice.stripe_invoice_id.slice(-8)}
                            </Text>
                          </VStack>
                        </Td>
                        <Td isNumeric>
                          <Text fontWeight="bold" fontSize="lg">
                            ${invoice.amount_paid.toFixed(2)}
                          </Text>
                          <Text fontSize="xs" color="gray.600">
                            {invoice.currency?.toUpperCase() || "USD"}
                          </Text>
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={
                              invoice.status === "paid" ? "green" : "gray"
                            }
                            fontSize="sm"
                            px={3}
                            py={1}
                            textTransform="capitalize"
                          >
                            {invoice.status}
                          </Badge>
                        </Td>
                        <Td>
                          {invoice.hosted_invoice_url && (
                            <Button
                              as="a"
                              href={invoice.hosted_invoice_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              size="sm"
                              colorScheme="blue"
                              variant="outline"
                              rightIcon={<FiExternalLink />}
                            >
                              View Invoice
                            </Button>
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </CardBody>
          </Card>
        )}
      </VStack>
    </Container>
  );
}
